import columnBuilder from "common/agGrid/columnBuilder";
import RenderParameters from "common/agGrid/renderParameters";
import AsyncButton from "common/ui/AsyncButton";
import { CustomField, CustomFieldOption } from "domain/customField";
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { AgGridReact } from "@ag-grid-community/react";
import defaultGridOptions from "common/agGrid/defaultGridOptions";
import _ from "lodash";

export interface CustomFieldOptionsModalProps {
    customField: CustomField;
    showModal: boolean;

    onSubmit(customFieldOptions: CustomFieldOption[]): void;
    onHide: () => void;
}

const CustomFieldOptionsModal: React.FC<CustomFieldOptionsModalProps> = props => {
    const [customFieldOptions, setCustomFieldOptions] = useState<CustomFieldOption[]>([]);

    useEffect(() => {
        if (props.customField)
            setCustomFieldOptions(props.customField.allowedOptions ?? []);
    }, [props.customField]);

    function getColumns() {
        return [
            columnBuilder.build("#", null,
                {
                    valueGetter: (params: any) => `Custom Field Option #${params.data.sortOrder}`,
                    minWidth: 150,
                    maxWidth: 150,
                }),
            columnBuilder.build("ERP Id", "erpId",
                {
                    cellRenderer: function (params: RenderParameters<CustomFieldOption, string>) {
                        return <Form.Control
                            defaultValue={params.node.data.erpId}
                            placeholder={"ERP Id"}
                            required
                            onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => handleCustomFieldOptionUpdate({ ...params.node.data, erpId: ev.target.value })} />;
                    },
                    minWidth: 200,
                    maxWidth: 200,
                }),
            columnBuilder.build("Description", "description",
                {
                    cellRenderer: function (params: RenderParameters<CustomFieldOption, string>) {
                        return <Form.Control
                            defaultValue={params.node.data.description}
                            placeholder={"Description"}
                            required
                            onBlur={(ev: React.ChangeEvent<HTMLInputElement>) => handleCustomFieldOptionUpdate({ ...params.node.data, description: ev.target.value })} />;
                    },
                    minWidth: 200,
                    maxWidth: 200,
                }),
            columnBuilder.build("", null,
                {
                    cellRenderer: function (params: RenderParameters<CustomFieldOption>) {
                        return <button className="link-button" onClick={() => handleCustomFieldOptionRemove(params.node.data)}>
                            <i className="material-icons">delete</i>
                        </button>;
                    },
                    minWidth: 50,
                    maxWidth: 50,
                }),
        ];
    }

    function handleCustomFieldOptionAdd(): void {
        const maxSortOrder = _.maxBy(customFieldOptions, cfo => cfo.sortOrder)?.sortOrder ?? 0;
        const sortOrder = maxSortOrder + 1;
        const newCustomFieldOption: CustomFieldOption = {
            erpId: "",
            description: "",
            sortOrder: sortOrder,
        };

        setCustomFieldOptions([...customFieldOptions, newCustomFieldOption]);
    }

    function handleCustomFieldOptionUpdate(customFieldOption: CustomFieldOption): void {
        setCustomFieldOptions(customFieldOptions.map(cfo => cfo.sortOrder === customFieldOption.sortOrder ? customFieldOption : cfo));
    }

    function handleCustomFieldOptionRemove(customFieldOption: CustomFieldOption): void {
        setCustomFieldOptions(customFieldOptions.filter(cfo => cfo.sortOrder !== customFieldOption.sortOrder));
    }

    async function onSaveClicked(): Promise<void> {
        props.onSubmit(customFieldOptions);
    }

    return <Modal show={props.showModal} dialogClassName="modal-lg" onHide={props.onHide} backdrop="static">
        <Modal.Header closeButton>
            <Modal.Title>{props.customField?.name} Custom Field Option</Modal.Title>
        </Modal.Header>

        <Modal.Body>
            <div className="ag-theme-alpine">
                <div className="row table-header-row ml-0 mr-0">
                    <div className="col action-bar">
                        <button className="btn btn-link ml-auto" onClick={handleCustomFieldOptionAdd}>
                            <i className="material-icons">add_circle_outline</i>
                            Add Custom Field Option
                        </button>
                    </div>
                </div>

                <AgGridReact
                    {...defaultGridOptions}
                    columnDefs={getColumns()}
                    rowHeight={32}
                    rowData={customFieldOptions}
                    getRowId={params => `customFieldOption-${params.data.id}-${params.data.sortOrder}`}
                    domLayout="autoHeight"
                    onGridReady={params => params.api.sizeColumnsToFit()}
                />
            </div>
        </Modal.Body>

        <Modal.Footer className="align center">
            <Button variant="secondary" onClick={props.onHide}>
                Cancel
            </Button>
            <AsyncButton variant="primary" onClickAsync={onSaveClicked}>
                Save
            </AsyncButton>
        </Modal.Footer>
    </Modal>;
};

export default CustomFieldOptionsModal;